import React, { useState } from "react";
import { Box, useTheme } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { tokens } from "../theme";

/**
 * Shared markdown rendering for the manual — used by both the help drawer and
 * the /help page so they cannot drift apart.
 *
 * `onChapterLink` receives a chapter slug when the reader follows an in-manual
 * cross reference (`[Predictions](#predictions)`); return value is ignored.
 * Pass `chapterSlugs` so only real chapters are treated as cross references.
 */

/** A screenshot that has not been captured yet must not render as a broken icon. */
const HelpImage = ({ src, alt }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <Box
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
          my: 2, py: 4, px: 2, borderRadius: 2, textAlign: "center",
          border: `1px dashed ${colors.grey[600]}`, color: colors.grey[400], fontSize: ".85rem",
        }}
      >
        <ImageOutlinedIcon fontSize="small" />
        {alt || "Screenshot coming soon"}
      </Box>
    );
  }

  return (
    <img
      src={src}
      alt={alt || ""}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{
        display: "block", width: "100%", height: "auto", margin: "16px 0",
        borderRadius: 8, border: `1px solid ${colors.grey[700]}`,
      }}
    />
  );
};

export function useHelpRenderers({ chapterSlugs = [], onChapterLink }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return {
    a: ({ href, children }) => {
      if (href && href.startsWith("#")) {
        const slug = href.slice(1);
        if (chapterSlugs.includes(slug)) {
          return (
            <a
              href={`/help/${slug}`}
              onClick={(e) => { e.preventDefault(); onChapterLink && onChapterLink(slug); }}
              style={{ color: colors.blueAccent[400], cursor: "pointer" }}
            >
              {children}
            </a>
          );
        }
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: colors.blueAccent[400] }}>
          {children}
        </a>
      );
    },
    img: ({ src, alt }) => <HelpImage src={src} alt={alt} />,
    table: ({ children }) => (
      <Box sx={{ overflowX: "auto", mb: 2 }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>{children}</table>
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{
          m: "20px 0", p: "12px 16px", borderRadius: "0 8px 8px 0",
          borderLeft: `3px solid ${colors.blueAccent[500]}`,
          // The grey scale is inverted between modes, so 900 reads as "one step
          // further from the card" in both light and dark.
          backgroundColor: colors.grey[900],
          "& p:last-child": { mb: 0 },
        }}
      >
        {children}
      </Box>
    ),
  };
}

const HelpMarkdown = ({ children, chapterSlugs, onChapterLink }) => {
  const components = useHelpRenderers({ chapterSlugs, onChapterLink });
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  );
};

export default HelpMarkdown;
